import { useCallback, useMemo } from 'react';

import { useEventKeys } from '@/feature/event-details-sbk/hooks/use-event';
import { useMarketCacheStore } from '@/feature/event-details-sbk/hooks/use-market-cache';
import { Market, MarketCategory } from '@/feature/event-details-sbk/types';
import { filterMarketsForProduct } from '@/feature/event-details-sbk/utils/is-supplier-for-product';
import { upsertMarketCategories } from '@/feature/event-details-sbk/utils/upsert-market-category';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { useSubscribePublic } from '@/utils/websocket/hooks';
import { MarketUpdateMessage, OddsUpdateMessageOption } from '@/utils/websocket/types';
import { runStoreUpdate } from '@/utils/zustand';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Subscribe to 'event:{eventId}:marketGroup:{marketGroupId}' channel and listens for `market_update` messages.
 * Updates the main line market in the market cache.
 * Updates the options in the market cache.
 * Adds markets the market categories query doesn't know yet (line moves arrive as `market_update` with a new market id).
 */
export const useSubscribeEventMarketUpdate = (eventId: string, marketGroupId: string, enabled: boolean = true) => {
    const handleOddsUpdate = useMarketCacheStore(state => state.actions.handleOddsUpdate);
    const updateMainLineMarkets = useMarketCacheStore(state => state.actions.updateMainLineMarkets);
    const isPredictions = useIsPredictionsProduct();
    const queryClient = useQueryClient();
    const eventKeys = useEventKeys();

    const upsertMarkets = useCallback(
        (markets: Market[]) => {
            const published = markets.filter(market => market.published);
            if (published.length === 0) {
                return;
            }
            const queryKey = eventKeys.marketCategories(eventId, marketGroupId);
            const categories = queryClient.getQueryData<MarketCategory[]>(queryKey);
            if (!categories) {
                return;
            }
            const next = upsertMarketCategories(categories, published);
            if (next !== categories) {
                queryClient.setQueryData(queryKey, next);
            }
        },
        [queryClient, eventKeys, eventId, marketGroupId]
    );

    const handleMarketUpdate = useCallback(
        (incomingMessages: MarketUpdateMessage[]) => {
            const messages = filterMarketsForProduct(incomingMessages, isPredictions);
            const oddsUpdateMessages: OddsUpdateMessageOption[] = messages.flatMap(message =>
                message.options.map(option => ({
                    id: option.id,
                    marketId: message.id,
                    odds: option.odds,
                    status: option.status,
                    isMainLine: message.is_main_line,
                    published: message.published,
                    modifiers: [],
                }))
            );
            runStoreUpdate(() => {
                handleOddsUpdate(oddsUpdateMessages);
                updateMainLineMarkets(messages);
            });
            upsertMarkets(messages);
        },
        [handleOddsUpdate, updateMainLineMarkets, upsertMarkets, isPredictions]
    );

    const params = useMemo(() => ({ eventId, marketGroupId }), [eventId, marketGroupId]);
    useSubscribePublic<'eventPopularMarketUpdate'>(
        'event:{eventId}:marketGroup:{marketGroupId}',
        params,
        'market_update',
        handleMarketUpdate,
        enabled
    );
};
